import React, { Fragment, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DropdownList } from 'react-widgets';
import { toastr } from 'react-redux-toastr';
import api from '../../../../api';
import ErrorMessage from '../../../../components/text/ErrorMessage';
import { useLeadActivities } from '../../../../core/hooks/useLeadActivities';
import { combineDateAndTime } from '../../../../utils/CombineDateAndTime';
import { ActivityModel } from './add-activity';
import {
  CustomTimePeriod,
  isTimePeriodValid,
  ITimePeriodData,
} from '../../../../core/components/time-period/custom-time-period';
import RemoteDataDropDown from '../../../../components/dropdown/RemoteDataDropDown';
import { IFormFilterData } from './edit-event-popup';
import {
  createStartOfDayWithTimeZone,
  PaginatedApiResponse,
  TestDriveVehicle,
} from '../../../../core';
import TestDriveVehicleModelVariantDropdown from '../../../../core/components/vehicle/TestDriveVehicleModelVariantDropdown';
import { useDataLoader } from 'react-remote-data-hooks';
import { TimeReservationModel } from './time-reservation-form';

interface ITimeReservationFormProps {
  leadId?: string;
  activity?: TimeReservationModel;
  onCancel?: () => void;
  onDateChange?: (date: Date) => void;
  onTimePeriodChange?: (data?: ITimePeriodData) => void;
  alwaysShowform?: boolean;
  onSuccess?: () => void;
  onChangeAssignee?: (id: string) => void;
  onEditSubmit?: (activity: ActivityModel) => void;
  isEdit?: boolean;
  onDataChange?: (data: IFormFilterData) => void;
}

export const VehicleTimeReservationForm: React.FunctionComponent<ITimeReservationFormProps> = (
  props
) => {
  const activity = props.activity;
  const {
    control,
    handleSubmit,
    errors,
    watch,
    setValue,
  } = useForm<VehicleTimeReservationModel>({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
  });
  const loadShowroom = async () => {
    const response = await api.settings.getShowrooms({ ecommerceOnly: true });
    return response;
  };
  const isActivityEditing = props.isEdit ?? false;
  const { createVehicleTimeReservation, updating } = useLeadActivities(
    api.lead
  );
  const onSubmit = async (data: TimeReservationModel) => {
    if (!isActivityEditing) {
      const startDate = combineDateAndTime(
        createStartOfDayWithTimeZone(data.timePeriod?.startDate!),
        data.timePeriod?.startTime!
      );
      const endDate = combineDateAndTime(
        createStartOfDayWithTimeZone(
          data.timePeriod?.endDate ?? data.timePeriod?.startDate!
        ),
        data.timePeriod?.endTime!
      );
      const apiModel: TimeReservationAPIModel = {
        startDate,
        endDate,
        note: data.note,
        busyEvent: data.busy,
        vehicleId: getVehicleId(data.vehicle)!,
      };
      createVehicleTimeReservation(apiModel, () => {
        toastr.success('Success', 'Time reservation created');
        if (props.onSuccess) {
          props.onSuccess();
        }
      });
    } else {
      if (activity != null) {
        const editedActivity: ActivityModel = {
          activityType: 'vehicleTimeOff',
          timePeriod: data.timePeriod,
          vehicleId: getVehicleId(data.vehicle),
          leadId: props.leadId as any,
          note: data.note as any,
          busyEvent: data.busy,
        };
        if (props.onEditSubmit) {
          props.onEditSubmit(editedActivity);
        }
      }
    }
  };

  const getVehicleId = (vehicle: any) => {
    return isObject(vehicle) ? vehicle._id : vehicle;
  };
  const isObject = (value: any) => {
    if (value != null && value.hasOwnProperty('_id')) {
      return true;
    }
    return false;
  };

  const handleDataChange = () => {
    if (props.onDataChange) {
      const salesperson: any = watch('salesPerson');
      const showroomData: any = watch('showroom');
      const vehicle: any = watch('vehicle');
      const data: IFormFilterData = {
        salesPerson: isObject(salesperson) ? salesperson._id : salesperson,
        vehicle: isObject(vehicle) ? vehicle._id : vehicle,
        showRoom: isObject(showroomData) ? showroomData._id : showroomData,
      };
      props.onDataChange(data);
    }
  };
  const showroom = isObject(watch('showroom'))
    ? (watch('showroom') as any)._id
    : watch('showroom');
  const modelCode = watch('modelCode') ?? activity?.vehicleModel?.modelCode;
  const variantCode =
    watch('variantCode') ?? activity?.vehicleVariant?.variantCode;
  const year = watch('year') ?? activity?.vehicleVariant?.year;

  const { data: vehicles } = useDataLoader<
    PaginatedApiResponse<TestDriveVehicle>
  >(() => api.vehicle.getTestDriveVehicleList());

  const filteredVehicles = vehicles?.data?.filter(
    (vh) =>
      vh.year === year &&
      vh.vehicleModel.modelCode === modelCode &&
      vh.vehicleModel.year === year &&
      vh.vehicleVariant.variantCode === variantCode &&
      vh.vehicleVariant.year === year &&
      vh.showroomId === showroom
  );

  return (
    <Fragment>
      <div
        className="tab-pane fade  show active"
        id="activity-all"
        role="tabpanel"
        aria-labelledby="activity-tab"
      >
        <div className="mt-4">
          <form
            id={'vehicle-time-reservation-form'}
            onSubmit={handleSubmit(onSubmit)}
          >
            <div
              key={JSON.stringify(activity?.showroom)}
              className="form-group font-weight-semibold mb-3"
            >
              <label htmlFor="" className="font-sm text-uppercase">
              Office<span className="text-danger">*</span>
              </label>
              <Controller
                control={control}
                rules={{ required: true }}
                name="showroom"
                defaultValue={activity?.showroom}
                render={({ onChange, value, name }) => (
                  <RemoteDataDropDown
                    loadData={loadShowroom}
                    textField="title"
                    value={value}
                    defaultValue={value}
                    valueField="_id"
                    onChange={(val) => {
                      onChange(val._id);
                      setValue('vehicle', undefined);
                      handleDataChange();
                    }}
                    placeholder="Select Office"
                  ></RemoteDataDropDown>
                )}
              />
              {errors.showroom && (
                <div>
                  <ErrorMessage>Office is required</ErrorMessage>
                </div>
              )}
            </div>

            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                Date and Time<span className="text-danger">*</span>
              </label>
              <div key={JSON.stringify(activity?.timePeriod ?? {})}>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name="timePeriod"
                  defaultValue={activity?.timePeriod}
                  render={({ onChange, value, name }) => (
                    <CustomTimePeriod
                      onChange={(val: ITimePeriodData) => {
                        if (isTimePeriodValid(val)) {
                          onChange(val);
                          if (props.onTimePeriodChange) {
                            props.onTimePeriodChange(val);
                          }
                        } else {
                          onChange(undefined);
                          if (props.onTimePeriodChange) {
                            props.onTimePeriodChange(undefined);
                          }
                        }
                      }}
                      defaultValue={activity?.timePeriod}
                    ></CustomTimePeriod>
                  )}
                />
              </div>
              {errors.timePeriod && (
                <ErrorMessage>Please enter a valid time period</ErrorMessage>
              )}
            </div>

            <div
              key={
                JSON.stringify(props.activity?.vehicleModel) +
                JSON.stringify(props.activity?.vehicleVariant)
              }
              className="form-group font-weight-semibold mb-3"
            >
              <label htmlFor="" className="font-sm text-uppercase">
                Model
              </label>
              <Controller
                control={control}
                name="modelCode"
                defaultValue={props.activity?.vehicleModel?.modelCode}
                render={() => <></>}
              />
              <Controller
                control={control}
                name="variantCode"
                defaultValue={props.activity?.vehicleVariant?.variantCode}
                render={() => <></>}
              />
              <Controller
                control={control}
                name="year"
                defaultValue={props.activity?.year}
                render={() => <></>}
              />

              <div key={'model-for-showroom-' + showroom}>
                <TestDriveVehicleModelVariantDropdown
                  data={
                    (vehicles?.data ?? [])?.filter(
                      (p) => p.showroomId === showroom
                    ) as any
                  }
                  default={
                    modelCode && year
                      ? { modelCode, year, variantCode }
                      : undefined
                  }
                  onChange={(data) => {
                    setValue('modelCode', data?.modelCode);
                    setValue('year', data?.year);
                    setValue('vehicle', undefined);
                    setValue('variantCode', data.variantCode);
                    handleDataChange();
                  }}
                ></TestDriveVehicleModelVariantDropdown>
                {(errors.modelCode || errors.year) && (
                  <ErrorMessage>Model is required</ErrorMessage>
                )}
              </div>
              <div
                key={'vehicle-type' + JSON.stringify(activity?.vehicle)}
                className="form-group font-weight-semibold mb-3"
              >
                <label htmlFor="" className="font-sm text-uppercase">
                  Vehicle<span className="text-danger">*</span>
                </label>

                <Controller
                  control={control}
                  rules={{ required: true }}
                  name="vehicle"
                  defaultValue={activity?.vehicle}
                  render={({ onChange, value, name }) => (
                    <DropdownList
                      value={value}
                      data={filteredVehicles?.map((veh) => {
                        return {
                          _id: veh._id,
                          displayName: `${veh.vehicleModel.modelCode}-${
                            veh.registrationNo
                          } ${veh.genericColor || ''}`,
                        };
                      })}
                      defaultValue={value}
                      textField={'displayName'}
                      valueField={'_id'}
                      placeholder="Select vehicle"
                      onChange={(val) => {
                        onChange(val._id);
                        handleDataChange();
                        setValue(
                          'variantCode',
                          filteredVehicles?.find(
                            (x) => x._id?.toString() === val._id?.toString()
                          )?.vehicleVariant?.variantCode
                        );
                      }}
                      filter={'contains'}
                    />
                  )}
                />

                {errors.vehicle && (
                  <ErrorMessage>Vehicle is required</ErrorMessage>
                )}
              </div>
            </div>

            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                TYPE
              </label>
              <Controller
                control={control}
                name="busy"
                defaultValue={activity?.busy || true}
                render={({ onChange, value, name }) => (
                  <DropdownList
                    value={value}
                    data={[
                      { label: 'Busy', value: true },
                      { label: 'Free', value: false },
                    ]}
                    textField={'label'}
                    valueField={'value'}
                    placeholder={'Select Type'}
                    onChange={(val) => {
                      onChange(val.value);
                    }}
                  />
                )}
              />
            </div>
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                REASON<span className="text-danger">*</span>
              </label>
              <Controller
                control={control}
                rules={{ required: true }}
                name="note"
                defaultValue={activity?.note}
                render={({ onChange, value, name }) => (
                  <textarea
                    rows={4}
                    className="form-control rounded-sm font-weight-semibold"
                    value={value}
                    onChange={onChange}
                    placeholder={'Comments'}
                  />
                )}
              />
              {errors.note && <ErrorMessage>Reason is required</ErrorMessage>}
            </div>
          </form>
          <div className="text-right">
            <button
              onClick={() => {
                if (props.onCancel) {
                  props.onCancel();
                }
              }}
              className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
              disabled={updating}
            >
              Cancel
            </button>
            <button
              disabled={updating}
              type="submit"
              form="vehicle-time-reservation-form"
              className="btn btn-primary py-2 font-weight-semibold font-normal"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export interface VehicleTimeReservationModel {
  note?: string;
  busy?: boolean;
  showroom?: string;
  modelCode: string;
  variantCode: string;
  timePeriod?: ITimePeriodData;
  year: string;
  vehicle: string;
}

export interface TimeReservationAPIModel {
  startDate: Date;
  endDate: Date;
  note?: string;
  busyEvent?: boolean;
  assignedPersonId?: string;
  vehicleId?: string;
  timeOffType?: string;
}
